/**
 * The readable JSON layout (Q-133 (b)): how DMW writes JSON that people read.
 *
 * Two-space indent; keys sorted unless `sortKeys` is false; a container that holds only
 * scalars on one line when that line, with its indent, key and comma, fits in
 * READABLE_JSON_WIDTH columns, otherwise one member per line; `[]` and `{}` for empty
 * containers; text as it is; no NaN or infinity. A stored file adds one trailing newline.
 * Columns are Unicode code points, and keys sort by code point.
 * Numbers use ECMAScript Number spelling (RFC 8785 section 3.2.2.3).
 *
 * Twin of the server's `dmw.kernel.json_forms.readable`. This file exists twice, byte for
 * byte: `connector/src/storage/readableJson.ts` and `frontend/src/shared/formatting/readableJson.ts`
 * (the packages share no code). Both run `schema/readable-json/golden.json` and the same
 * behavioural cases for non-JSON values and sparse arrays.
 */
export const READABLE_JSON_WIDTH = 100;
const INDENT = "  ";

export interface ReadableJsonOptions {
  readonly sortKeys?: boolean;
}

type Member = readonly [head: string, value: unknown];

/** The readable text of `value`, without a trailing newline. */
export function readableJsonText(value: unknown, options: ReadableJsonOptions = {}): string {
  const plain = plainValue(value);
  if (plain === undefined) throw new TypeError("JSON has no form for this value.");
  return text(plain, options.sortKeys ?? true, 0, 0);
}

/** `taken`: the columns the value's first and last lines share with its indent, key and comma. */
function text(value: unknown, sortKeys: boolean, depth: number, taken: number): string {
  if (Array.isArray(value)) {
    const members = Array.from(value, (item: unknown): Member => ["", plainValue(item) ?? null]);
    return container("[", "]", members, sortKeys, depth, taken);
  }
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    if (sortKeys) keys.sort(compareCodePoints);
    const members = keys.flatMap((key): Member[] => {
      const member = plainValue(record[key]);
      return member === undefined ? [] : [[`${JSON.stringify(key)}: `, member]];
    });
    return container("{", "}", members, sortKeys, depth, taken);
  }
  return scalar(value);
}

function container(opening: string, closing: string, members: readonly Member[], sortKeys: boolean, depth: number, taken: number): string {
  if (members.length === 0) return opening + closing;
  if (members.every(([, member]) => isScalar(member))) {
    const line = opening + members.map(([head, member]) => head + scalar(member)).join(", ") + closing;
    if (taken + columns(line) <= READABLE_JSON_WIDTH) return line;
  }
  const indent = INDENT.repeat(depth + 1);
  const lines = members.map(([head, member], index) => {
    const comma = index < members.length - 1 ? "," : "";
    return indent + head + text(member, sortKeys, depth + 1, columns(indent + head + comma)) + comma;
  });
  return `${opening}\n${lines.join("\n")}\n${INDENT.repeat(depth)}${closing}`;
}

/** As JSON.stringify sees a value: `toJSON` applied; `undefined` for what it leaves out. */
function plainValue(value: unknown): unknown {
  if (value !== null && typeof value === "object" && "toJSON" in value && typeof value.toJSON === "function") {
    return (value.toJSON as () => unknown).call(value);
  }
  return typeof value === "function" || typeof value === "symbol" ? undefined : value;
}

function isScalar(value: unknown): boolean {
  return value === null || typeof value !== "object";
}

function scalar(value: unknown): string {
  if (typeof value === "number" && !Number.isFinite(value)) throw new RangeError("JSON has no NaN or infinity.");
  return JSON.stringify(value);
}

function columns(line: string): number {
  let count = 0;
  for (let index = 0; index < line.length; index += 1) {
    const unit = line.charCodeAt(index);
    if (unit < 0xdc00 || unit > 0xdfff) count += 1; // a low surrogate ends the character before it
  }
  return count;
}

/** Code point order: UTF-16 orders astral characters (surrogates) before U+E000–U+FFFF. */
function compareCodePoints(left: string, right: string): number {
  const length = Math.min(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const difference = codePointRank(left.charCodeAt(index)) - codePointRank(right.charCodeAt(index));
    if (difference !== 0) return difference;
  }
  return left.length - right.length;
}

function codePointRank(unit: number): number {
  if (unit >= 0xd800 && unit <= 0xdfff) return unit + 0x2000;
  return unit >= 0xe000 ? unit - 0x800 : unit;
}
